import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Button,
  TextField,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
  Paper,
  Typography,
  ThemeProvider,
  CssBaseline,
  Container,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Link as MuiLink,
  Stack,
} from '@mui/material';
import AppleIcon from '@mui/icons-material/Apple';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useMutation } from 'react-query';
import { GoogleLogin } from '@react-oauth/google';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';
import { sha256 } from 'js-sha256';

import client from '../../API';
import routes from '../../Config/routes';
import { validateEmail, validatePassword } from '../../Utils';
import { lightTheme } from '../../Utils/baseTheme';
import useAuth from '../../Hooks/use-auth';
import { useCreatorAuth } from '../../Hooks/creator-use-auth';
import useAlertDialog from '../../Components/useAlertDialog';
import AlertDialog from '../../Components/AlertDialog';
import HomeHeader from '../../Components/HomeHeader';
import HomeFooter from '../../Components/HomeFooter';
import { setAuthToken } from '../../API/token-util';
import {
  initAuthStorage,
  getAuthToken as getUserToken,
} from '../../API/token-util';
import {
  initCreatorAuthStorage,
  getAuthToken as getCreatorToken,
} from '../../API/creatorTokenUtil';

/* -------------------------------------------------------------------------- */
/*  Google OAuth constants                                                    */
/* -------------------------------------------------------------------------- */
const GOOGLE_IOS_CLIENT_ID =
  '489265257959-374fsc04dffos29tto4jsqcd2sch5f9o.apps.googleusercontent.com';
const GOOGLE_SCOPES = ['profile', 'email'];
const REDIRECT_URI =
  'com.googleusercontent.apps.489265257959-374fsc04dffos29tto4jsqcd2sch5f9o:/oauth2redirect';

/* -------------------------------------------------------------------------- */
/*  PKCE helpers                                                              */
/* -------------------------------------------------------------------------- */
const base64url = (uint8) =>
  btoa(String.fromCharCode(...uint8))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

function createPKCE() {
  const rnd = crypto.getRandomValues(new Uint8Array(32));
  const verifier = base64url(rnd);
  const hash = sha256.arrayBuffer(verifier);
  const challenge = base64url(new Uint8Array(hash));
  return { verifier, challenge };
}

/* -------------------------------------------------------------------------- */
/*  Exchange “code” → “id_token”                                              */
/* -------------------------------------------------------------------------- */
async function exchangeCodeForIdToken(code, codeVerifier) {
  const body = new URLSearchParams({
    code,
    client_id: GOOGLE_IOS_CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    grant_type: 'authorization_code',
    code_verifier: codeVerifier,
  }).toString();

  try {
    const res = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error_description || 'Google token exchange failed');
    }
    const data = await res.json();
    if (!data.id_token) throw new Error('No id_token returned from Google');
    return data.id_token;
  } catch (err) {
    console.error('[Google OAuth] Token exchange error:', err);
    throw err;
  }
}

/* -------------------------------------------------------------------------- */
/*  Component                                                                 */
/* -------------------------------------------------------------------------- */
export default function UnifiedLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  /* ------------------------------ state ------------------------------------ */
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'info' });
  const [oauthAsk, setOauthAsk] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isAppleLoading, setIsAppleLoading] = useState(false);
  const [isLoadingTokens, setIsLoadingTokens] = useState(true);
  const pkceRef = useRef(null);
  const tabOpen = useRef(false);

  const { authorize, setCurrentUser } = useAuth();
  const { login: creatorLogin } = useCreatorAuth();
  const { dialogState } = useAlertDialog();

  const redirect = searchParams.get('redirect');
  const service_id = searchParams.get('service_id');
  const stateParam = searchParams.get('state');
  const isWeb = Capacitor.getPlatform() === 'web';

  /* -------------------- helper: add device-token if present ---------------- */
  const getBestToken = async () => {
    const stored = window._pendingApnsToken;
    if (stored) return stored;
    const { value } = await Preferences.get({ key: 'apns_token' });
    return value;
  };

  const attachDevice = async (payload) => {
    if (Capacitor.getPlatform() === 'web') return payload;
    const token = await getBestToken();
    console.info('[UnifiedLogin] Device token for login:', token || 'none');
    if (token) {
      return { ...payload, device_token: token, platform: 'ios' };
    }
    console.warn('[UnifiedLogin] No device token available for login');
    return payload;
  };

  /* ----------------------------- mutations --------------------------------- */
  const handleLoginSuccess = (data) => {
    if (tabOpen.current) Browser.close().catch(() => {});
    setIsGoogleLoading(false);
    setIsAppleLoading(false);

    if (data.user.global_user_type === 'creator') {
      creatorLogin({ token: data.token, creator_user: data.user });
      navigate(data.redirect === 'edit' ? routes.creatorConnectEdit : routes.creatorConnectStart);
    } else {
      setAuthToken({ token: data.token, user: data.user });
      authorize(data.token);
      setCurrentUser(data.user);
      goAfterLogin(data.redirect);
    }
  };

  const onError = (err, provider = 'Login') => {
    const msg = err?.response?.data?.error ?? err?.message ?? 'An unknown error occurred.';
    console.error(`[${provider} Login] Error:`, msg, err);
    setSnack({ open: true, sev: 'error', msg });
    setIsGoogleLoading(false);
    setIsAppleLoading(false);
  };

  const { mutate: unifiedLogin, isLoading: isUnifiedLoading } = useMutation(
    async (payload) => {
      const enhancedPayload = await attachDevice(payload);
      if (!enhancedPayload.token && !enhancedPayload.email) {
        throw new Error('Missing token or email for login');
      }
      return client.users.uniloginV2(enhancedPayload);
    },
    { onSuccess: handleLoginSuccess, onError }
  );

  const { mutate: appleLogin, isLoading: isAppleApiLoading } = useMutation(
    async (payload) => {
      const enhancedPayload = await attachDevice(payload);
      if (!enhancedPayload.identityToken) {
        throw new Error('Missing Apple identity token');
      }
      return client.users.appleLoginv2(enhancedPayload);
    },
    { onSuccess: handleLoginSuccess, onError: (e) => onError(e, 'Apple') }
  );

  /* --------------------------- deep-link handler --------------------------- */
  const handleDeepLink = useCallback(
    async ({ url }) => {
      try {
        const u = new URL(url);
        if (
          u.protocol.startsWith('com.googleusercontent.apps.') &&
          u.pathname === '/oauth2redirect'
        ) {
          if (tabOpen.current) {
            await Browser.close().catch(() => {});
            tabOpen.current = false;
          }

          const error = u.searchParams.get('error');
          if (error) throw new Error(`Google OAuth error: ${error}`);

          const code = u.searchParams.get('code');
          if (!code) throw new Error('Missing OAuth code');

          setIsGoogleLoading(true);
          const idToken = await exchangeCodeForIdToken(code, pkceRef.current?.verifier);
          if (!idToken) throw new Error('Failed to obtain Google id_token');
          unifiedLogin({ token: idToken });
        }
      } catch (err) {
        console.error('[UnifiedLogin] Deep link error:', err);
        setSnack({ open: true, sev: 'error', msg: err.message || 'Google login failed' });
        setIsGoogleLoading(false);
      }
    },
    [unifiedLogin]
  );

  /* ----------------------------- side-effects ------------------------------ */
  useEffect(() => {
    if (service_id) setOauthAsk(true);
  }, [service_id]);

  useEffect(() => {
    if (!isWeb) {
      const appListener = App.addListener('appUrlOpen', handleDeepLink);
      const browserListener = Browser.addListener('browserFinished', () => {
        tabOpen.current = false;
        setIsGoogleLoading(false);
      });
      return () => {
        appListener.remove();
        browserListener.remove();
      };
    }
  }, [isWeb, handleDeepLink]);

  /* ----------------------- token-based redirection ------------------------- */
  useEffect(() => {
    const checkTokensAndRedirect = async () => {
      try {
        // Hydrate both token stores
        await Promise.all([initAuthStorage(), initCreatorAuthStorage()]);

        // Check for tokens
        const creatorJWT = getCreatorToken();
        const userJWT = getUserToken();

        // Redirect if tokens exist
        if (creatorJWT || userJWT) {
          const startPath = creatorJWT
            ? routes.creatorConnectStart
            : routes.dashboard;
          navigate(startPath, { replace: true });
        }
      } catch (error) {
        console.debug("Error checking tokens:", error);
      } finally {
        setIsLoadingTokens(false);
      }
    };

    checkTokensAndRedirect();
  }, [navigate]);

  /* ----------------------- navigation helper ------------------------------- */
  const goAfterLogin = (loginRedirect) => {
    if (redirect?.startsWith('/')) return navigate(redirect);
    if (loginRedirect === 'edit') return navigate(routes.creatorConnectEdit);
    if (redirect === 'oauth') {
      const q = new URLSearchParams({ service_id, state: stateParam ?? '' });
      return navigate(`${routes.creatorConnectOauth}?${q}`);
    }
    navigate(routes.dashboard);
  };

  /* --------------------------- form handlers ------------------------------- */
  const emailLogin = (e) => {
    e.preventDefault();
    if (!validateEmail(email)) {
      setSnack({ open: true, sev: 'error', msg: 'Invalid email' });
      return;
    }
    if (!validatePassword(password)) {
      setSnack({ open: true, sev: 'error', msg: 'Weak password' });
      return;
    }
    unifiedLogin({ email, password });
  };

  const webGoogleSuccess = (res) => {
    setIsGoogleLoading(true);
    unifiedLogin({ token: res.credential });
  };

  const webGoogleFail = (err) => {
    setSnack({ open: true, sev: 'error', msg: err.error ?? 'Google login failed' });
  };

  /* ---------------------------- native Google ------------------------------ */
  const nativeGoogle = async () => {
    try {
      pkceRef.current = createPKCE();
      const { challenge } = pkceRef.current;

      tabOpen.current = true;
      setIsGoogleLoading(true);
      await Browser.open({
        url: `https://accounts.google.com/o/oauth2/v2/auth?client_id=${encodeURIComponent(
          GOOGLE_IOS_CLIENT_ID
        )}&redirect_uri=${encodeURIComponent(
          REDIRECT_URI
        )}&response_type=code&code_challenge=${encodeURIComponent(
          challenge
        )}&code_challenge_method=S256&scope=${encodeURIComponent(
          GOOGLE_SCOPES.join(' ')
        )}&include_granted_scopes=true&access_type=offline&prompt=select_account`,
        presentationStyle: 'fullscreen',
      });
    } catch (err) {
      console.error('[UnifiedLogin] Native Google login error:', err);
      setSnack({ open: true, sev: 'error', msg: err.message || 'Google login cancelled' });
      setIsGoogleLoading(false);
      tabOpen.current = false;
    }
  };

  /* ---------------------------- Apple sign-in ------------------------------ */
  const handleApple = async () => {
    if (isWeb) {
      setSnack({
        open: true,
        sev: 'info',
        msg: 'Apple login is available only in the iOS app',
      });
      return;
    }
    try {
      setIsAppleLoading(true);
      const { SignInWithApple } = await import('@capacitor-community/apple-sign-in');
      const credential = await SignInWithApple.authorize({ scopes: ['email', 'name'] });
      const identityToken = credential.identityToken ?? credential?.response?.identityToken;
      if (!identityToken) throw new Error('Apple identity token missing');
      appleLogin({ identityToken });
    } catch (err) {
      console.error('[UnifiedLogin] Apple login error:', err);
      setSnack({ open: true, sev: 'error', msg: err.message || 'Apple login cancelled' });
      setIsAppleLoading(false);
    }
  };

  /* ------------------------------- UI -------------------------------------- */
  const anyLoading =
    isUnifiedLoading || isGoogleLoading || isAppleLoading || isAppleApiLoading;

  if (isLoadingTokens) {
    return null; // Avoid rendering until token check is complete
  }

  return (
    <ThemeProvider theme={lightTheme}>
      <CssBaseline />
      <AlertDialog alertState={dialogState} />

      {/* CreatorConnect OAuth prompt */}
      <Dialog open={oauthAsk} onClose={() => setOauthAsk(false)}>
        <DialogTitle>Connecting to an App with Blitz</DialogTitle>
        <DialogContent>
          <DialogContentText>
            To use this App, you’ll need a CreatorConnect account.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            component={Link}
            to={`${routes.creatorConnectOnboardApp}?service_id=${service_id ?? ''}&state=${
              stateParam ?? ''
            }`}
          >
            I’m new
          </Button>
          <Button variant="text" onClick={() => setOauthAsk(false)}>
            I already have one
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <HomeHeader />
        <Toolbar />
        <Container
          maxWidth="sm"
          sx={{ my: 6, flex: 1, display: 'flex', alignItems: 'center' }}
        >
          <Paper
            elevation={4}
            sx={{
              p: { xs: 4, md: 6 },
              width: '100%',
              borderRadius: 6,
              textAlign: 'center',
            }}
          >
            <Typography variant="h4" fontWeight={700} sx={{ mb: 3 }}>
              Sign in to Blitz
            </Typography>

            <Stack spacing={4}>
              {/* Google ---------------------------------------------------- */}
              <Box>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                  Creators & Advanced Users
                </Typography>
                {isWeb ? (
                  <GoogleLogin
                    onSuccess={webGoogleSuccess}
                    onError={webGoogleFail}
                    disabled={anyLoading}
                    theme="filled_blue"
                    size="large"
                    ux_mode="popup"
                    width="320"
                  />
                ) : (
                  <Button
                    variant="contained"
                    size="large"
                    onClick={nativeGoogle}
                    disabled={anyLoading}
                    startIcon={isGoogleLoading && <CircularProgress size={20} />}
                    sx={{ px: 5, py: 1.75, fontSize: '1rem' }}
                  >
                    Sign in with Google
                  </Button>
                )}
              </Box>

              {/* Apple ----------------------------------------------------- */}
              <Box>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleApple}
                  disabled={anyLoading}
                  startIcon={
                    isAppleLoading || isAppleApiLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <AppleIcon />
                    )
                  }
                  sx={{
                    backgroundColor: '#000',
                    '&:hover': { backgroundColor: '#333' },
                    py: 1.75,
                    fontSize: '1rem',
                  }}
                >
                  Sign in with Apple
                </Button>
              </Box>

              {/* Email / Password ----------------------------------------- */}
              <Box component="form" onSubmit={emailLogin} noValidate>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                  Brands & Agencies (Email)
                </Typography>
                <TextField
                  fullWidth
                  required
                  autoComplete="email"
                  label="Email"
                  margin="normal"
                  value={email}
                  disabled={anyLoading}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <TextField
                  fullWidth
                  required
                  type="password"
                  autoComplete="current-password"
                  label="Password"
                  margin="normal"
                  value={password}
                  disabled={anyLoading}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mt: 1 }}
                >
                  <MuiLink component={Link} to={routes.passwordForgot} underline="hover">
                    Forgot password?
                  </MuiLink>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={anyLoading}
                    startIcon={isUnifiedLoading && <CircularProgress size={20} />}
                  >
                    Login
                  </Button>
                </Stack>
              </Box>

              {/* CTA ------------------------------------------------------- */}
              <Box>
                <Typography variant="body2">Don’t have an account?</Typography>
                <Button onClick={() => navigate(routes.requestAccess)} sx={{ mt: 1 }}>
                  Contact Us
                </Button>
              </Box>
            </Stack>
          </Paper>
        </Container>
        <HomeFooter />
      </Box>

      {/* Snackbar ------------------------------------------------------------- */}
      <Snackbar
        open={snack.open}
        autoHideDuration={6000}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert severity={snack.sev} sx={{ width: '100%' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}